import { useState } from 'react'

import {
  hasActionPermission,
  hasInputPermission,
} from '@tribeplatform/gql-client'
import {
  CustomFieldPrivacyOptions,
  CustomFieldSchema,
  CustomFieldType,
  CustomFieldValidators,
} from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-sdk/components'
import {
  useAddMemberSchemaField,
  useNetwork,
  useUnArchiveMemberSchemaField,
  useUpdateMemberSchemaField,
} from '@tribeplatform/react-sdk/hooks'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { MultiselectCreatable } from '@tribeplatform/react-ui-kit/Multiselect'

import { PlanBadge } from '../PlanBadge'
import { FieldTypePicker } from './FieldTypePicker'
import {
  CustomFieldSubtype,
  fieldTypes,
  getFieldIcon,
  getFieldSetting,
  getFieldTypeName,
} from './utils'

export const FieldModal = ({
  field: initialField,
  open,
  onClose,
}: {
  field: Partial<CustomFieldSchema>
  open: boolean
  onClose: () => void
}) => {
  const { mutateAsync: updateField, isLoading: isUpdating } =
    useUpdateMemberSchemaField()
  const { mutateAsync: addField, isLoading: isAdding } =
    useAddMemberSchemaField()
  const { mutateAsync: unArchiveField, isLoading: isUnarchiving } =
    useUnArchiveMemberSchemaField()
  const [field, setField] = useState<Partial<CustomFieldSchema>>(initialField)
  const { data: network } = useNetwork()

  const { actionPermission } =
    hasActionPermission(
      network?.authMemberProps?.permissions ?? [],
      // Remove fallback when fixed on backend
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      'addCustomFieldSchema',
    ) ||
    hasActionPermission(
      network?.authMemberProps?.permissions ?? [],
      'addMemberSchemaField',
    )

  const { authorized: readPrivacyAuthorized, requiredPlan: readPrivacyPlan } =
    hasInputPermission(actionPermission.inputPermissions, 'readPrivacy')
  const { authorized: writePrivacyAuthorized, requiredPlan: writePrivacyPlan } =
    hasInputPermission(actionPermission.inputPermissions, 'writePrivacy')

  let settings = { subtype: undefined }
  if (field?.settings && field?.settings.length) {
    settings = field.settings.reduce(
      (result, cur) => {
        try {
          result[cur.key] = JSON.parse(cur.value)
        } catch (e) {
          logger.error('Error parsing field setting value', e.message)
        }
        return result
      },
      { subtype: undefined },
    )
  }

  const permission = { read: 'everyone', write: 'own' }
  if (field?.readPrivacy?.allow) {
    permission.read =
      field?.readPrivacy?.allow?.indexOf(CustomFieldPrivacyOptions.ADMIN) !== -1
        ? 'admin'
        : 'own'
  }
  if (field?.writePrivacy?.allow) {
    permission.write =
      field?.writePrivacy?.allow?.indexOf(CustomFieldPrivacyOptions.OWN) !== -1
        ? 'own'
        : 'admin'
  }

  let enumValidator = field?.validators?.find(
    validator => validator.validation === CustomFieldValidators.ENUM,
  )
  if (!enumValidator) {
    enumValidator = field?.items?.validators?.find(
      validator => validator.validation === CustomFieldValidators.ENUM,
    )
  }
  let initialOptions = []
  if (enumValidator) {
    initialOptions = JSON.parse(enumValidator.value)
  }
  const [options, setOptions] = useState(initialOptions)

  const onSubmit = values => {
    const { key, type, name, permission, settings } = values

    const input: CustomFieldSchema = {
      key,
      name,
      type,
      settings: [],
      validators: [],
    }
    if (field?.items) input.items = field?.items

    if (
      permission?.read &&
      permission?.read !== 'everyone' &&
      readPrivacyAuthorized
    ) {
      input.readPrivacy = {
        allow:
          permission.read === 'admin'
            ? [CustomFieldPrivacyOptions.ADMIN]
            : [CustomFieldPrivacyOptions.OWN, CustomFieldPrivacyOptions.ADMIN],
      }
    }

    if (permission?.write && writePrivacyAuthorized) {
      input.writePrivacy = {
        allow:
          permission.write === 'admin'
            ? [CustomFieldPrivacyOptions.ADMIN]
            : [CustomFieldPrivacyOptions.OWN, CustomFieldPrivacyOptions.ADMIN],
      }
    }
    if (settings) {
      Object.keys(settings).forEach(key => {
        if (settings[key] !== null && typeof settings[key] !== 'undefined') {
          input.settings.push({
            key,
            value: JSON.stringify(settings[key]),
          })
        }
      })
    }

    if (options) {
      if (settings.subtype === CustomFieldSubtype.SELECT) {
        const enumValidator = field?.validators?.find(
          validator => validator.validation === CustomFieldValidators.ENUM,
        )
        if (enumValidator) {
          enumValidator.value = JSON.stringify(options)
        } else {
          if (!field.validators) field.validators = []
          field.validators.push({
            validation: CustomFieldValidators.ENUM,
            value: JSON.stringify(options),
          })
        }
      } else if (settings.subtype === CustomFieldSubtype.MULTISELECT) {
        field.validators = []
        const enumValidator = field?.items?.validators?.find(
          validator => validator.validation === CustomFieldValidators.ENUM,
        )
        if (enumValidator) {
          enumValidator.value = JSON.stringify(options)
        } else {
          if (!field?.items)
            field.items = {
              key: 'options',
              name: 'Options',
              type: CustomFieldType.TEXT,
            }

          if (!field?.items?.validators) field.items.validators = []
          field.items.validators.push({
            validation: CustomFieldValidators.ENUM,
            value: JSON.stringify(options),
          })
        }
      }
    }

    input.validators = field?.validators
    if (field?.items) {
      if (typeof field?.items?.required !== 'undefined')
        delete field.items.required
      if (typeof field?.items?.description !== 'undefined')
        delete field.items.description
      input.items = field?.items
    }

    if (field?.key) {
      delete input.type
      return updateField({ input }).then(() => {
        onClose()
      })
    }
    return addField({ input: { ...input, type: field.type } }).then(() => {
      onClose()
    })
  }

  const getModalTitle = field => {
    if (field?.key) return `Update field (${field.key})`
    if (field?.type) return `Add ${getFieldTypeName(field)} field`
    return 'Add field'
  }

  const supportedFieldTypes = fieldTypes.filter(
    type => type.type === field?.type,
  )

  const locked = getFieldSetting(field, 'locked')

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!field.type || !field.name) return onClose()
        if (window.confirm('Are you sure you want to discard your changes?')) {
          onClose()
        }
      }}
    >
      <Modal.Header title={getModalTitle(field)} />
      <Modal.Content>
        {!field.type ? (
          <FieldTypePicker
            onChange={field =>
              setField({
                name: '',
                key: field.key,
                type: field.type,
                validators: field.validators,
                settings: field.settings,
              })
            }
          />
        ) : (
          <Form
            onSubmit={onSubmit}
            defaultValues={{
              ...field,
              ...{
                settings,
                permission,
              },
            }}
            className="flex flex-col space-y-3"
          >
            {supportedFieldTypes.length > 1 && (
              <Form.Select
                label="Type"
                name="settings.subtype"
                disabled={locked}
                onChange={subtype => {
                  const newType = fieldTypes.find(
                    type => getFieldSetting(type, 'subtype') === subtype,
                  )

                  if (newType) {
                    if (newType?.settings)
                      field.settings = [...newType.settings]
                    if (newType?.validators)
                      field.validators = [...newType.validators]
                    setField({
                      ...field,
                    })
                  }
                }}
                items={supportedFieldTypes.map(type => ({
                  text: (
                    <div className="flex space-x-2 items-center">
                      <span className="w-5 h-5">{getFieldIcon(type)}</span>
                      <span>{type.name}</span>
                    </div>
                  ),
                  value: getFieldSetting(type, 'subtype') || undefined,
                }))}
                helperText={
                  locked
                    ? 'This field has a custom validation and its type cannot be changed'
                    : ''
                }
              />
            )}
            <Form.Input label="Name" name="name" />
            {!field?.key && (
              <Form.Input
                label="Key"
                name="key"
                helperText="Key is the unique identifier for this field and you will not be able to change it in the future"
                validation={{
                  required: "This field can't be empty",
                  pattern: {
                    value: /^[a-zA-Z_]+$/,
                    message: 'Key can only include alphabets, _, and _',
                  },
                }}
              />
            )}
            {[
              CustomFieldSubtype.SELECT,
              CustomFieldSubtype.MULTISELECT,
            ].indexOf(settings.subtype) !== -1 && (
              <div>
                <label
                  className="block font-medium text-basicSurface-700"
                  htmlFor="options"
                >
                  Options
                </label>
                <div className="mt-1">
                  <MultiselectCreatable
                    value={options}
                    options={[]}
                    onChange={setOptions}
                    searchable
                    onCreateItem={inputValue => {
                      setOptions([...options, inputValue])
                    }}
                  >
                    <MultiselectCreatable.Button placeholder="Add options...">
                      {options.map((person, index) => (
                        <MultiselectCreatable.SelectedItem
                          key={person}
                          value={person}
                          index={index}
                        >
                          {person}
                        </MultiselectCreatable.SelectedItem>
                      ))}
                    </MultiselectCreatable.Button>
                    <MultiselectCreatable.Items>
                      {({ creating, inputValue }) => {
                        return (
                          <>
                            {creating && inputValue?.length > 0 && (
                              <MultiselectCreatable.Item
                                value={inputValue}
                                index={options.length}
                              >
                                <span>Create</span>{' '}
                                <span className="font-bold">{inputValue}</span>
                              </MultiselectCreatable.Item>
                            )}
                          </>
                        )
                      }}
                    </MultiselectCreatable.Items>
                  </MultiselectCreatable>
                </div>
              </div>
            )}

            <Accordion>
              <Accordion.Button>Advanced options</Accordion.Button>
              <Accordion.Panel className="flex-1 space-y-3">
                {field.type !== CustomFieldType.BOOLEAN &&
                  field.type !== CustomFieldType.DATE && (
                    <Form.Input
                      label={
                        <>
                          Placeholder <small>(optional)</small>
                        </>
                      }
                      name="settings.placeholder"
                    />
                  )}
                <Form.Input
                  label={
                    <>
                      Helper text <small>(optional)</small>
                    </>
                  }
                  helperText="A helper text explaining the field like this one"
                  name="settings.helperText"
                />
                <Form.Select
                  label={
                    <div className="flex space-x-1">
                      <span>Who can see this field?</span>
                      <PlanBadge
                        size="sm"
                        requiredPlanName={readPrivacyPlan}
                        authorized={readPrivacyAuthorized}
                      />
                    </div>
                  }
                  name="permission.read"
                  disabled={!readPrivacyAuthorized}
                  items={[
                    { text: 'Everyone', value: 'everyone' },
                    { text: 'Owner and admins', value: 'own' },
                    { text: 'Only admins', value: 'admin' },
                  ]}
                />
                <Form.Select
                  label={
                    <div className="flex space-x-1">
                      <span>Who can update this field?</span>
                      <PlanBadge
                        size="sm"
                        requiredPlanName={writePrivacyPlan}
                        authorized={writePrivacyAuthorized}
                      />
                    </div>
                  }
                  name="permission.write"
                  disabled={!writePrivacyAuthorized}
                  items={[
                    { text: 'Owner and admins', value: 'own' },
                    { text: 'Only admins', value: 'admin' },
                  ]}
                />
              </Accordion.Panel>
            </Accordion>

            <Form.Actions className="pt-3">
              <Button loading={isAdding || isUpdating} size="lg" type="submit">
                {field?.key ? 'Update' : 'Create'}
              </Button>
              {field.archived && (
                <Button
                  variant="outline"
                  loading={isUnarchiving}
                  onClick={async () => {
                    await unArchiveField({ key: field.key })
                    onClose()
                  }}
                  size="lg"
                >
                  Unarchive
                </Button>
              )}
            </Form.Actions>
          </Form>
        )}
      </Modal.Content>
    </Modal>
  )
}
